import { FC, forwardRef, PropsWithChildren, useMemo, useCallback } from 'react';

import { useRouter } from 'next/router';

import { cx } from 'class-variance-authority';

import { map as mapA } from 'fp-ts/lib/Array';
import { pipe } from 'fp-ts/lib/function';
import {
  alt,
  fromPredicate,
  map,
  sequenceArray,
  toUndefined,
} from 'fp-ts/lib/Option';
import { BaseTypeMap, Component, ComponentProps } from '../types/Component';

import { PaginationOptions, usePagination } from './usePagination';

import { Box } from '../Box';
import { Typography } from '../Typography';

type PaginationTypeMap = BaseTypeMap<
  'div',
  PaginationOptions & {
    onPageChange?: (n: number) => void;
    nextLabel: string;
    prevLabel: string;
    goToLabel: (n: number) => string;
  }
>;

export type PaginationProps = ComponentProps<PaginationTypeMap>;

const Item: FC<PropsWithChildren<{ active?: boolean }>> = ({ children }) => (
  <Box
    as="li"
    className={cx('flex h-10 w-10 items-center justify-center rounded-full')}
  >
    {children}
  </Box>
);

export const Pagination: Component<PaginationTypeMap> = forwardRef(
  function Pagination(
    {
      className,
      onPageChange,
      count,
      siblings,
      boundary,
      current = 1,
      nextLabel,
      prevLabel,
      goToLabel,
      ...rest
    },
    ref
  ) {
    const router = useRouter();

    const items = usePagination({
      count,
      siblings,
      boundary,
      current,
    });

    const handlePageChange = useCallback(
      (pageNumber: number) => {
        onPageChange
          ? onPageChange(pageNumber)
          : router.push(
              {
                pathname: router.pathname,
                query: { ...router.query, page: pageNumber },
              },
              undefined,
              { shallow: true }
            );
      },
      [router, onPageChange]
    );

    const nextBtn = useMemo(
      () => (
        <Item>
          <button
            className={cx(
              'text-primary-700 hover:bg-primary-700 flex h-10 w-10 items-center justify-center rounded-full text-2xl hover:text-white',
              current === count || count < 2 ? 'hidden' : undefined
            )}
            onClick={() => handlePageChange(current + 1)}
            aria-label={nextLabel}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="currentColor"
              aria-hidden="true"
              viewBox="0 0 24 24"
              className="h-6 w-6"
            >
              <g mask="#mask0_6691_14014">
                <path d="M8.7 17.3a.948.948 0 0 1-.275-.7c0-.283.092-.517.275-.7l3.9-3.9-3.9-3.9a.948.948 0 0 1-.275-.7c0-.283.092-.517.275-.7a.948.948 0 0 1 .7-.275c.283 0 .517.092.7.275l4.6 4.6c.1.1.171.208.213.325.041.117.062.242.062.375s-.02.258-.062.375a.883.883 0 0 1-.213.325l-4.6 4.6a.948.948 0 0 1-.7.275.948.948 0 0 1-.7-.275Z" />
              </g>
            </svg>
          </button>
        </Item>
      ),
      [count, current, handlePageChange, nextLabel]
    );

    const prevBtn = useMemo(
      () => (
        <Item>
          <button
            className={cx(
              'text-primary-700 hover:bg-primary-700 flex h-10 w-10 items-center justify-center rounded-full text-2xl hover:text-white',
              current < 2 && count >= 0
                ? 'hidden'
                : 'h-10 w-10 rounded-full !p-0'
            )}
            onClick={() => handlePageChange(current - 1)}
            aria-label={prevLabel}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="currentColor"
              aria-hidden="true"
              className="h-6 w-6 rotate-180"
              viewBox="0 0 24 24"
            >
              <g mask="#mask0_6691_14014">
                <path d="M8.7 17.3a.948.948 0 0 1-.275-.7c0-.283.092-.517.275-.7l3.9-3.9-3.9-3.9a.948.948 0 0 1-.275-.7c0-.283.092-.517.275-.7a.948.948 0 0 1 .7-.275c.283 0 .517.092.7.275l4.6 4.6c.1.1.171.208.213.325.041.117.062.242.062.375s-.02.258-.062.375a.883.883 0 0 1-.213.325l-4.6 4.6a.948.948 0 0 1-.7.275.948.948 0 0 1-.7-.275Z" />
              </g>
            </svg>
          </button>
        </Item>
      ),
      [count, current, handlePageChange, prevLabel]
    );

    const content = useMemo(
      () =>
        pipe(
          items,
          mapA((item) =>
            pipe(
              item,
              fromPredicate((x) => x.type === 'previous'),
              map(() => prevBtn),
              alt(() =>
                pipe(
                  item,
                  fromPredicate((x) => x.type === 'next'),
                  map(() => nextBtn)
                )
              ),
              alt(() =>
                pipe(
                  item,
                  fromPredicate(
                    (x) =>
                      x.type === 'start-ellipsis' || x.type === 'end-ellipsis'
                  ),
                  map(() => (
                    <Item>
                      <span className="text-primary-700 select-none text-2xl font-semibold">
                        ...
                      </span>
                    </Item>
                  ))
                )
              ),
              alt(() =>
                pipe(
                  item,
                  fromPredicate((x) => typeof x.type === 'number'),
                  map(({ active, type }) => (
                    <Item active={active}>
                      <button
                        onClick={() => handlePageChange(type as number)}
                        className={cx(
                          'hover:bg-primary-700 h-full w-full justify-center rounded-full text-2xl hover:text-white',
                          active
                            ? 'bg-primary-700'
                            : 'text-primary-600 border-0 bg-white'
                        )}
                        aria-current={active ? 'true' : 'false'}
                        aria-label={goToLabel(type as number)}
                      >
                        <Typography
                          size="lg"
                          color={active ? 'white' : undefined}
                          weight="bold"
                        >
                          {type}
                        </Typography>
                      </button>
                    </Item>
                  ))
                )
              )
            )
          ),
          sequenceArray,
          toUndefined
        ),
      [items, prevBtn, nextBtn, handlePageChange, goToLabel]
    );

    return (
      <Box
        ref={ref}
        className={cx('flex gap-0 sm:gap-4', className)}
        {...rest}
        as="ul"
      >
        {content}
      </Box>
    );
  }
);
